import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  TextField,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  fetchOnboardingData,
  updateOnboardingData,
} from "../../actions/onboardingAction";
import { uploadVideo, clearMedia } from "../../actions/mediaActions";
import VideoContainerPreview from '../Common/VideoContainerPreview';
import { preventEnterFormSubmission } from '../../utils/formUtils';

const OnboardingData = ({ onActionComplete, formRef }) => {
  const dispatch = useDispatch();
  const video = useSelector(state => state.media.video);
  const onboardingData = useSelector(state => state.onboarding_data.onboarding_data);

  const [loading, setLoading] = useState(true);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [formData, setFormData] = useState({
    onboarding_video_one_url: "",
    onboarding_video_one_title: "",
    video_one_onboarding_length: "",
    video_one_onboarding_length_in_sec: 0,
    onboarding_video_two_url: "",
    onboarding_video_two_title: "",
    video_two_onboarding_length: "",
    video_two_onboarding_length_in_sec: 0,
    onboarding_video_three_url: "",
    onboarding_video_three_title: "",
    video_three_onboarding_length: "",
    video_three_onboarding_length_in_sec: 0,
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(fetchOnboardingData());
      } catch (error) {
        console.error('Error loading data:', error);
        onActionComplete(false, 'Error loading data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch]);

  useEffect(() => {
    if (onboardingData) {
      const processVideoLength = async (videoNumber) => {
        const url = onboardingData[`onboarding_video_${videoNumber}_url`];
        if (!url) return;

        return new Promise((resolve) => {
          const videoElement = document.createElement('video');
          videoElement.src = url;
          videoElement.onloadedmetadata = () => {
            const duration = Math.round(videoElement.duration);
            const minutes = Math.floor(duration / 60);
            const seconds = duration % 60;
            const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
            resolve({
              length: formattedDuration,
              lengthInSec: duration
            });
          };
        });
      };

      const updateFormData = async () => {
        const [videoOne, videoTwo, videoThree] = await Promise.all([
          processVideoLength('one'),
          processVideoLength('two'),
          processVideoLength('three')
        ]);

        setFormData({
          onboarding_video_one_url: onboardingData.onboarding_video_one_url || "",
          onboarding_video_one_title: onboardingData.onboarding_video_one_title || "",
          video_one_onboarding_length: videoOne?.length || "",
          video_one_onboarding_length_in_sec: videoOne?.lengthInSec || 0,
          onboarding_video_two_url: onboardingData.onboarding_video_two_url || "",
          onboarding_video_two_title: onboardingData.onboarding_video_two_title || "",
          video_two_onboarding_length: videoTwo?.length || "",
          video_two_onboarding_length_in_sec: videoTwo?.lengthInSec || 0,
          onboarding_video_three_url: onboardingData.onboarding_video_three_url || "",
          onboarding_video_three_title: onboardingData.onboarding_video_three_title || "",
          video_three_onboarding_length: videoThree?.length || "",
          video_three_onboarding_length_in_sec: videoThree?.lengthInSec || 0,
        });
      };

      updateFormData();
    }
  }, [onboardingData]);

  useEffect(() => {
    if (video) {
      setIsProcessingVideo(true);
      const processVideo = async (videoNumber) => {
        return new Promise((resolve) => {
          const videoElement = document.createElement('video');
          videoElement.src = video;
          videoElement.onloadedmetadata = () => {
            const duration = Math.round(videoElement.duration);
            const minutes = Math.floor(duration / 60);
            const seconds = duration % 60;
            const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;

            setFormData(prev => ({
              ...prev,
              [`onboarding_video_${videoNumber}_url`]: video,
              [`video_${videoNumber}_onboarding_length`]: formattedDuration,
              [`video_${videoNumber}_onboarding_length_in_sec`]: duration,
            }));
            resolve();
          };
        });
      };

      const updateVideo = async () => {
        if (formData.onboarding_video_one_uploading) {
          await processVideo('one');
        } else if (formData.onboarding_video_two_uploading) {
          await processVideo('two');
        } else if (formData.onboarding_video_three_uploading) {
          await processVideo('three');
        }
        dispatch(clearMedia());
        setIsProcessingVideo(false);
      };

      updateVideo();
    }
  }, [video, dispatch]);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleVideoUpload = (e, videoNumber) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData(prev => ({
      ...prev,
      [`onboarding_video_${videoNumber}_uploading`]: true,
    }));

    dispatch(uploadVideo(file));
  };

  const handleVideoRemove = (videoNumber) => {
    setFormData(prev => ({
      ...prev,
      [`onboarding_video_${videoNumber}_url`]: "",
      [`video_${videoNumber}_onboarding_length`]: "",
      [`video_${videoNumber}_onboarding_length_in_sec`]: 0,
    }));
  };

  const handleVideoMetadata = (videoNumber, formattedDuration, duration) => {
    setFormData(prev => ({
      ...prev,
      [`video_${videoNumber}_onboarding_length`]: formattedDuration,
      [`video_${videoNumber}_onboarding_length_in_sec`]: duration,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isProcessingVideo) {
      onActionComplete(false, 'Please wait for video processing to complete');
      return;
    }

    // Create a new object with only the fields that have been updated
    const updatedData = {
      onboarding_video_one_title: formData.onboarding_video_one_title,
      onboarding_video_two_title: formData.onboarding_video_two_title,
      onboarding_video_three_title: formData.onboarding_video_three_title,
    };

    // Only include video fields if they've been updated
    ['one', 'two', 'three'].forEach(num => {
      const hasUrl = formData[`onboarding_video_${num}_url`];
      updatedData[`onboarding_video_${num}_url`] = hasUrl ? formData[`onboarding_video_${num}_url`] : "";
      updatedData[`video_${num}_onboarding_length`] = hasUrl ? formData[`video_${num}_onboarding_length`] : "";
      updatedData[`video_${num}_onboarding_length_in_sec`] = hasUrl ? formData[`video_${num}_onboarding_length_in_sec`] : 0;
    });

    try {
      dispatch(updateOnboardingData(updatedData))
        .then(() => {
          onActionComplete(true, 'Tapping Basics data updated successfully');
          dispatch(fetchOnboardingData());
        });
    } catch (error) {
      onActionComplete(false, 'Error updating onboarding data');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} onKeyDown={preventEnterFormSubmission} ref={formRef}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, flexWrap: 'wrap' }}>
        {/* Video One */}
        <Card sx={{ flex: '1 1 30%', minWidth: '300px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tapping Basics Video 1
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Video 1 Title"
                name="onboarding_video_one_title"
                value={formData.onboarding_video_one_title}
                onChange={handleFieldChange}
                margin="normal"
              />
            </Box>
            <VideoContainerPreview
              title="Tapping Basics Video 1"
              video={formData.onboarding_video_one_url}
              name="onboarding_video_one"
              handleRemove={() => handleVideoRemove("one")}
              handleVideoUpload={(e) => handleVideoUpload(e, "one")}
              showCopyUrlButton={true}
              onMetadataLoaded={(formattedDuration, duration) => handleVideoMetadata("one", formattedDuration, duration)}
              sx={{
                width: '100%',
                background: '#FFFCF5',
                height: 400,
                '& video': {
                  objectFit: 'contain'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Video Two */}
        <Card sx={{ flex: '1 1 30%', minWidth: '300px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tapping Basics Video 2
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Video 2 Title"
                name="onboarding_video_two_title"
                value={formData.onboarding_video_two_title}
                onChange={handleFieldChange}
                margin="normal"
              />
            </Box>
            <VideoContainerPreview
              title="Tapping Basics Video 2"
              video={formData.onboarding_video_two_url}
              name="onboarding_video_two"
              handleRemove={() => handleVideoRemove("two")}
              handleVideoUpload={(e) => handleVideoUpload(e, "two")}
              showCopyUrlButton={true}
              onMetadataLoaded={(formattedDuration, duration) => handleVideoMetadata("two", formattedDuration, duration)}
              sx={{
                width: '100%',
                background: '#FFFCF5',
                height: 400,
                '& video': {
                  objectFit: 'contain'
                }
              }}
            />
          </CardContent>
        </Card>

        {/* Video Three */}
        <Card sx={{ flex: '1 1 30%', minWidth: '300px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Tapping Basics Video 3
            </Typography>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Video 3 Title"
                name="onboarding_video_three_title"
                value={formData.onboarding_video_three_title}
                onChange={handleFieldChange}
                margin="normal"
              />
            </Box>
            <VideoContainerPreview
              title="Tapping Basics Video 3"
              video={formData.onboarding_video_three_url}
              name="onboarding_video_three"
              handleRemove={() => handleVideoRemove("three")}
              handleVideoUpload={(e) => handleVideoUpload(e, "three")}
              showCopyUrlButton={true}
              onMetadataLoaded={(formattedDuration, duration) => handleVideoMetadata("three", formattedDuration, duration)}
              sx={{
                width: '100%',
                background: '#FFFCF5',
                height: 400,
                '& video': {
                  objectFit: 'contain'
                }
              }}
            />
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default OnboardingData;
